import { Box, useEffectOnValueChange, useMountEffect, usePrevious } from '@timelyapp/react';
import classNames from 'classnames';
import DatePickerCustomPeriod from 'components/datepicker/DatePickerCustomPeriod';
import {
  datePickerConfiguration,
  getModifiers,
  getSelectedDatesForMode,
  getSelectedDays,
  getSpanForRelativeDateRange,
  handleDisabledDays,
  sanitizeRange,
} from 'components/datepicker/utils';
import IntersectionObserver from 'components/IntersectionObserver';
import type { RefType as CustomScrollbarsRefType } from 'components/scrollbars';
import { Scrollbars } from 'components/scrollbars';
import { isString } from 'lodash-es';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DayModifiers, FunctionModifier } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleClientReportDatePickerRangeMode,
  toggleDatePickerDropdownRangeMode,
} from 'redux/modules/global.types';
import {
  getClientReportDatePickerIsRangeMode,
  getDatePickerDropdownIsRangeMode,
} from 'redux/selectors/global';
import type { ThunkDispatch } from 'types/redux';
import type { RawCapacity } from 'types/summaries';
import { getLastDayOfWeek, isSameWeek, isSameYear } from 'utils/date-helper';
import { fromISO, fromJSDate } from 'utils/luxon-helpers';
import uuidv4 from 'utils/uuid';
import type { StartOfWeek } from '../../types/account';
import styles from './DatePicker.module.scss';
import DatePickerFooter from './DatePickerFooter';
import DatePickerHeader from './DatePickerHeader';
import DayPickerCaption from './DayPickerCaption';
import DayPickerRelativeDateRangesSection from './DayPickerRelativeDateRangesSection';
import type { DayStatusMap } from './MemoizedDayPicker';
import MemoizedDayPicker from './MemoizedDayPicker';
import type {
  DatePickerMode,
  DatePickerModeIncludingCustom,
  DateRange,
  MaxRange,
  RelativeDateRange,
} from './types';

const today = DateTime.now().toISODate();

const defaultMaxRange: MaxRange = {
  type: 'years',
  value: 10,
};

export interface Props {
  startOfWeek: StartOfWeek;
  baseDate: string;
  dateRange?: DateRange;
  initialMode: DatePickerModeIncludingCustom;
  /** Returns the date (YYYY-MM-DD), as well as the from/to range based on mode */
  onDateChange: (date: string, range: DateRange) => void;
  onModeChanged?: (mode: DatePickerMode) => void;
  onCustomRangeEnabledChange?: (enabled: boolean) => void;
  disabledBefore?: string;
  disabledAfter?: string;
  disableNonWorkDays?: boolean;
  className?: string;
  disabled?: boolean;
  // the currently selected date range
  enabledModes?: Array<DatePickerMode>;
  maxRange?: MaxRange;
  enableRangeMode?: boolean;
  startNewRangeOnMount?: boolean;
  closeOnRangeSet?: boolean;
  showRelativeDateRanges?: boolean;
  relativeDateRange?: RelativeDateRange;
  onRelativeDateRangeSelect?: (
    e: React.SyntheticEvent<HTMLElement>,
    relativeDateRange: RelativeDateRange
  ) => void;
  isClientReport?: boolean;
  dayStatusMap?: DayStatusMap;
}

export interface RefProps extends CustomScrollbarsRefType {
  viewScrollTop: number;
}

const DatePicker: React.FC<Props> = props => {
  const {
    disabledBefore,
    disabledAfter,
    disableNonWorkDays,
    maxRange = defaultMaxRange,
    baseDate = today,
    startOfWeek,
    enabledModes,
    enableRangeMode = false,
    initialMode = 'week',
    onDateChange,
    onModeChanged,
    className,
    disabled = false,
    startNewRangeOnMount = false,
    onCustomRangeEnabledChange,
    showRelativeDateRanges = false,
    onRelativeDateRangeSelect,
    relativeDateRange,
    isClientReport = false,
    dayStatusMap,
  } = props;
  // the old version of the DatePicker had a "custom" mode which is now baked into "day" mode
  const resolvedMode = initialMode === 'custom' ? 'day' : initialMode;
  const [localRange, setLocalRange] = useState<DateRange>();
  const previousRelativeDateRange = usePrevious(relativeDateRange);
  const previousEnableRangeMode = usePrevious(enableRangeMode);
  const dateRange = localRange != null ? localRange : props.dateRange;

  useEffect(() => {
    if (
      relativeDateRange !== previousRelativeDateRange ||
      enableRangeMode !== previousEnableRangeMode
    ) {
      // reset state
      setLocalRange(undefined);
    }
  }, [enableRangeMode, previousEnableRangeMode, previousRelativeDateRange, relativeDateRange]);

  const handleDateChange = useCallback(
    (date: string, range: DateRange) => {
      if (enableRangeMode) {
        if (range.to) {
          onDateChange(date, range);
          setLocalRange(undefined);
        } else {
          setLocalRange(range);
        }
      } else {
        onDateChange(date, range);
      }
    },
    [enableRangeMode, onDateChange]
  );

  const getInitialOffset = (mode: DatePickerMode) => {
    const config = datePickerConfiguration[mode];
    return -Math.abs(config.scrollStep);
  };

  const scrollTo = useCallback((domId: string) => {
    const element = document.getElementById(domId);
    element?.scrollIntoView(true);
  }, []);

  const [mode, setMode] = useState<DatePickerMode>(resolvedMode);
  const [dateOffset, setDateOffset] = useState(() => getInitialOffset(resolvedMode));
  const [returnToDate, setReturnToDate] = useState(baseDate);
  const [selectedYear, setSelectedYear] = useState(() => fromISO(baseDate).toFormat('y'));
  const [enteredTo, setEnteredTo] = useState<string | null>(null);
  const [enteredWeek, setEnteredWeek] = useState<string | null>(null);
  const containerRef = useRef<RefProps>(null);
  const scopeId = useMemo(() => uuidv4(), []);
  const dispatch = useDispatch<ThunkDispatch>();
  const globalCustomDateRangeEnabled = useSelector(
    isClientReport ? getClientReportDatePickerIsRangeMode : getDatePickerDropdownIsRangeMode
  );
  const customDateRangeEnabled = enableRangeMode && globalCustomDateRangeEnabled;
  const toggleCustomDateRangeEnabled = useCallback(
    () =>
      dispatch(
        isClientReport
          ? toggleClientReportDatePickerRangeMode()
          : toggleDatePickerDropdownRangeMode()
      ),
    [dispatch, isClientReport]
  );

  const getConfig = useCallback(() => {
    return datePickerConfiguration[mode];
  }, [mode]);

  const getDisabledDays = useMemo(() => {
    const wrappedFn = () => {
      if (dateRange) {
        const { from, to } = sanitizeRange(baseDate, mode, startOfWeek, dateRange);

        return handleDisabledDays({
          disabledBefore,
          disabledAfter,
          disableNonWorkDays,
          dateRange: { from, to },
          maxRange: !dateRange?.to && customDateRangeEnabled ? maxRange : undefined,
        });
      }

      return () => false;
    };
    let cachedValue: ReturnType<typeof handleDisabledDays>;
    return () => (cachedValue ??= wrappedFn());
  }, [
    baseDate,
    customDateRangeEnabled,
    dateRange,
    disableNonWorkDays,
    disabledAfter,
    disabledBefore,
    maxRange,
    mode,
    startOfWeek,
  ]);

  useMountEffect(() => {
    const id = fromISO(baseDate).startOf('month').toISODate();
    scrollTo(id);

    if (dateRange) {
      // unset the range on mount if passed startNewRangeOnMount
      if (enableRangeMode && startNewRangeOnMount) {
        handleDateChange(dateRange.from, { from: dateRange.from, to: undefined });
      }
    }

    onCustomRangeEnabledChange?.(customDateRangeEnabled);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  });

  useEffectOnValueChange(mode, () => {
    if (dateRange) {
      const id = fromISO(dateRange?.from).startOf('month').toISODate();
      setReturnToDate(id);
      scrollTo(id);
    }
  });

  useEffectOnValueChange(relativeDateRange, () => {
    if (relativeDateRange && dateRange) {
      const span = getSpanForRelativeDateRange(relativeDateRange);
      if (enabledModes?.includes(span)) {
        setMode(span);
        const id = fromISO(dateRange?.from).startOf(span).toISODate();
        setReturnToDate(id);
        scrollTo(id);
      }
    }
  });

  useEffectOnValueChange(returnToDate, () => {
    const id = fromISO(returnToDate).startOf('month').toISODate();
    scrollTo(id);
  });

  useEffectOnValueChange(customDateRangeEnabled, () => {
    // handle turning on and off custom date range mode
    if (dateRange) {
      if (!customDateRangeEnabled) {
        const range = getSelectedDatesForMode(
          fromISO(dateRange.from).toJSDate(),
          mode,
          startOfWeek
        );
        handleDateChange(dateRange.from, range);
      } else {
        handleDateChange(dateRange.from, { from: dateRange.from, to: undefined });
      }
    }
    onCustomRangeEnabledChange?.(customDateRangeEnabled);
  });

  const handleWeekNumberClick = useCallback(
    (weekNumber: number, days: Array<Date>) => {
      const [start, , , , , , ,] = days;
      const dayString = fromJSDate(start).toISODate();

      if (mode !== 'week') {
        return;
      }
      const range = getSelectedDatesForMode(start, mode, startOfWeek);
      setEnteredTo(null);
      handleDateChange(dayString, range);
    },
    [mode, handleDateChange, startOfWeek]
  );

  const handleDayClick = useCallback(
    (day: Date, options: Partial<DayModifiers>) => {
      const dayString = fromJSDate(day).toISODate();
      const date = dayString;

      if (options.disabled) return;

      const range = getSelectedDatesForMode(
        day,
        mode,
        startOfWeek,
        customDateRangeEnabled ? dateRange : undefined
      );
      setEnteredTo(null);
      handleDateChange(date, range);
    },
    [customDateRangeEnabled, dateRange, mode, handleDateChange, startOfWeek]
  );

  const handleReturnToBaseDate = useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      setDateOffset(getInitialOffset(resolvedMode));
      setReturnToDate(DateTime.now().toISO());
      handleDayClick(DateTime.now().toJSDate(), {});
    },
    [handleDayClick, resolvedMode]
  );

  const handleDayMouseEnter = useCallback(
    (day: Date | string) => {
      const date = isString(day) ? day : fromJSDate(day).toISODate();

      let enteredWeek: string | null = null;
      let enteredTo: string | null = null;

      if (customDateRangeEnabled && dateRange && !dateRange.to) {
        const entered = fromISO(date).endOf(mode).toISODate();

        if (entered !== enteredTo) {
          enteredTo = entered;
        }
      }

      if (!customDateRangeEnabled && enteredTo) {
        enteredTo = null;
      }

      const isInWeekMode = mode === 'week';

      // keep track of what week we're hovering on, we use this to style both sides if the week spans across months
      if (isInWeekMode) {
        enteredWeek = getLastDayOfWeek(date, startOfWeek);
      }

      setEnteredWeek(enteredWeek);
      setEnteredTo(enteredTo);
    },
    [customDateRangeEnabled, dateRange, mode, startOfWeek]
  );

  const handleDayMouseLeave = () => {
    setEnteredWeek(null);
  };

  const handleChangeMode = useCallback(
    (mode: DatePickerMode) => {
      const { from, to } = sanitizeRange(
        baseDate,
        mode,
        startOfWeek,
        customDateRangeEnabled ? dateRange : undefined
      );
      setMode(mode);
      setDateOffset(getInitialOffset(mode));
      handleDateChange(from, { from, to });
      onModeChanged?.(mode);
    },
    [baseDate, customDateRangeEnabled, dateRange, handleDateChange, onModeChanged, startOfWeek]
  );

  const handleCalendarScrollTo = useCallback(
    (isIntersecting: boolean, scrollTowards: any) => {
      if (!isIntersecting || mode === 'year') return;

      const { scrollStep, rowHeight } = getConfig();

      const distanceToScroll = scrollStep * rowHeight;
      const containerScrollTop = containerRef?.current?.viewScrollTop || 0;

      // Default to scrollTowards === past
      let newDateOffset = dateOffset - scrollStep;
      let positionFromTop = containerScrollTop + distanceToScroll;

      if (scrollTowards === 'future') {
        newDateOffset = dateOffset + scrollStep;
        positionFromTop = containerScrollTop - distanceToScroll;
      }
      setDateOffset(newDateOffset);
      setTimeout(() => containerRef?.current?.scrollTop(positionFromTop), 10);
    },
    [dateOffset, getConfig, mode]
  );

  const handleMouseUp = useCallback(() => {
    if (containerRef.current?.viewScrollTop === 0) {
      handleCalendarScrollTo(true, 'past');
    }

    const scrollToFutureThreshold = containerRef.current?.getScrollHeight() ?? 0;
    const offset = (containerRef.current?.getValues().clientHeight ?? 0) + 50;

    if ((containerRef.current?.viewScrollTop ?? 0) > scrollToFutureThreshold - offset) {
      handleCalendarScrollTo(true, 'future');
    }
  }, [handleCalendarScrollTo]);

  const handleGetSelectedDays = useCallback(
    (day: Date) =>
      getSelectedDays(
        baseDate,
        fromJSDate(day).toISODate(),
        enteredTo,
        mode,
        startOfWeek,
        dateRange
      ),
    [baseDate, enteredTo, mode, startOfWeek, dateRange]
  );

  const { from, to } = useMemo(
    () => sanitizeRange(baseDate, mode, startOfWeek, dateRange),
    [baseDate, dateRange, mode, startOfWeek]
  );

  const disabledDays: FunctionModifier = getDisabledDays();

  const modifiers = getModifiers(
    baseDate,
    dateRange,
    { enteredTo, enteredWeek },
    mode,
    startOfWeek
  );

  const renderWeeks = () => {
    const isSingleWeek = mode === 'week' && to && isSameWeek(from, to, startOfWeek);

    const dayPickerClassName = classNames(styles.dayPickerContainer, {
      singleDay: mode !== 'week' && !customDateRangeEnabled,
      singleWeek: mode === 'week' && isSingleWeek,
      customDateRangeEnabled,
      customWeekRangeEnabled: mode === 'week' && customDateRangeEnabled,
      rangePreviewMode: customDateRangeEnabled && enteredTo && !dateRange?.to,
    });

    const startFrom = fromISO(returnToDate).startOf('month').plus({ months: dateOffset });
    const monthIndexes = Array.from(Array(12).keys());

    const toRender = (
      i: number,
      renderProps?: {
        userCapacity: Array<RawCapacity> | undefined;
      }
    ): React.ReactElement => {
      const month = new Date(startFrom.plus({ months: i }).toISODate());

      const dtMonth = fromJSDate(month);
      const monthString = dtMonth.startOf('month').toISODate();
      // https://stackoverflow.com/a/55050203 🙃
      const safariRenderHack: React.CSSProperties = {
        opacity: i % 2 ? 1 : 0.99,
        display: 'flex',
      };
      return (
        <IntersectionObserver
          key={i}
          onIntersectingChange={isIntersecting => {
            if (!isSameYear(monthString, selectedYear) && isIntersecting) {
              setSelectedYear(dtMonth.toFormat('y'));
            }
          }}
        >
          <>
            {month.getMonth() === 0 ? (
              <div className={styles.yearDivider}>
                {month.getFullYear()}
                <span />
              </div>
            ) : null}
            <div style={safariRenderHack}>
              <div id={monthString} style={{ position: 'relative', top: -40 }} />
              <DayPickerCaption date={month} />
              <MemoizedDayPicker
                className={dayPickerClassName}
                firstDayOfWeek={startOfWeek}
                captionElement={<></>}
                month={month}
                showOutsideDays
                showWeekNumbers
                showWeekDays={false}
                enableOutsideDaysClick={false}
                initialMonth={month}
                disabledDays={disabledDays}
                onDayClick={handleDayClick}
                onWeekClick={handleWeekNumberClick}
                onDayMouseEnter={handleDayMouseEnter}
                onDayMouseLeave={handleDayMouseLeave}
                selectedDays={handleGetSelectedDays}
                modifiers={modifiers}
                userCapacity={renderProps?.userCapacity}
                mode={mode}
                dateRange={dateRange}
                disabledBefore={disabledBefore}
                dayStatusMap={dayStatusMap}
              />
            </div>
          </>
        </IntersectionObserver>
      );
    };

    return (
      <div>
        <IntersectionObserver onIntersectingChange={handleCalendarScrollTo} context="past">
          <div style={{ height: 10 }} />
        </IntersectionObserver>
        {monthIndexes.map(i => toRender(i))}
        <IntersectionObserver onIntersectingChange={handleCalendarScrollTo} context="future">
          <div style={{ height: 10 }} />
        </IntersectionObserver>
      </div>
    );
  };

  const renderCustomPeriod = () => {
    return (
      <DatePickerCustomPeriod
        scopeId={scopeId}
        mode={mode}
        handleCalendarScrollTo={handleCalendarScrollTo}
        disabledBefore={disabledBefore}
        disabledAfter={disabledAfter}
        disableNonWorkDays={!!disableNonWorkDays}
        returnToDate={returnToDate}
        baseDate={baseDate}
        onDayClick={handleDayClick}
        dateRange={dateRange || { from: baseDate }}
        dateOffset={dateOffset}
        maxRange={customDateRangeEnabled ? maxRange : undefined}
        config={getConfig()}
        startOfWeek={startOfWeek}
        customDateRangeEnabled={customDateRangeEnabled}
        handleMouseEnter={handleDayMouseEnter}
        enteredTo={enteredTo}
      />
    );
  };

  const containerClass = classNames(styles.container, className, {
    [styles.disabledContainer]: disabled,
  });

  const contents = (
    <>
      {['day', 'week'].includes(mode) && (
        <DatePickerHeader
          showRelativeDateRanges={showRelativeDateRanges}
          baseDate={baseDate}
          startOfWeek={startOfWeek}
          selectedYear={selectedYear}
          onYearChange={selectedYear => {
            setSelectedYear(selectedYear);
            setReturnToDate(`${selectedYear}-01-01`);
            setDateOffset(0);
          }}
        />
      )}
      {['month', 'year'].includes(mode) && renderCustomPeriod()}
      {['day', 'week'].includes(mode) && renderWeeks()}
    </>
  );

  // Triggers and stuff:
  return (
    <Box alignItems="flex-start" style={{ width: showRelativeDateRanges ? 611 : 'auto' }}>
      <div
        className={containerClass}
        style={
          showRelativeDateRanges
            ? {
                borderRight: '1px solid var(--theme_color_content_divider)',
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }
            : undefined
        }
      >
        {mode === 'year' ? (
          <div>{contents}</div>
        ) : (
          <Scrollbars
            autoHeight
            autoHeightMin={400}
            autoHeightMax={400}
            onScrollStart={() => document.addEventListener('mouseup', handleMouseUp)}
            ref={containerRef}
          >
            {contents}
          </Scrollbars>
        )}

        <DatePickerFooter
          enabledModes={enabledModes}
          mode={mode}
          onChangeMode={handleChangeMode}
          canToggleRangeMode={enableRangeMode}
          customDateRangeEnabled={customDateRangeEnabled}
          onChangeCustomRangeMode={toggleCustomDateRangeEnabled}
          onReturnToBaseDate={handleReturnToBaseDate}
        />
      </div>
      {showRelativeDateRanges && (
        <DayPickerRelativeDateRangesSection
          relativeDateRange={relativeDateRange}
          onRelativeDateRangeSelect={onRelativeDateRangeSelect}
        />
      )}
    </Box>
  );
};

export default DatePicker;
